import type { VersionedDraftKit } from '@/lib/versioned-draft';
import type {
  ExperienceContent,
  ExperienceDetail,
  ExperienceDraftContent,
  ExperiencePageDraft,
  ExperiencePersona,
  ExperienceTheme,
  NoyauTextsRef,
  VersionPin,
  VersionedRef,
} from '@matter_extractor/shared';
import {
  DEFAULT_PAGE_TRANSITION,
  EXPERIENCE_PAGES_MAX,
  FIRST_TEMPS_START,
  conversationRunOf,
  experienceContentSchema,
  feedbackPageObstacle,
  newExperienceContent,
  noyauBlocksOf,
  withFeedbackPage,
} from '@matter_extractor/shared';

/**
 * What the Expériences editor holds while somebody types, and the page-level
 * operations its timeline asks for (refonte modulaire §4, §9.1).
 *
 * The comparison that greys « Enregistrer », the version numbering and the six
 * common refusals are in `versioned-draft.ts` and hold for every versioned
 * object. What is here is the SHAPE of an experience's draft, the two functions
 * that cross between it and the wire, and every PAGE EDIT the scene of §9.1
 * performs - add, insert, remove, the protocol of the protocol page, the theme
 * and a page's overrides. They are pure and they are here, because the page
 * editor of #58 performs several of them from another screen, and an insertion
 * written twice would be two contracts.
 *
 * ## Why the draft is not an `ExperienceContent`
 *
 * An `ExperienceContent` is a version: its name is non-empty, every page is
 * named, and every button, checkbox and input carries a label. A DRAFT is a
 * half-written thought - a page somebody has just added and not renamed, a
 * button whose label is being typed. Keeping the two shapes apart is what lets
 * the editor render exactly what was typed while `normalizedContent` decides,
 * once, what would be sent.
 *
 * ## `normalizedContent` ASKS THE SCHEMA rather than re-deciding
 *
 * The criterion's and the protocol's normalisers judge four strings by hand,
 * because four is what their content requires. An experience's content is a
 * tree - pages, temps, blocks, six block variants, two structural refinements -
 * and a hand-rolled judgement here would be a second implementation of
 * `experienceContentSchema`, drifting from it the day a block gains a field.
 * So the draft is TRIMMED where a draft is allowed to hold blanks the version
 * is not, and then handed to the schema: `safeParse` answering `false` IS
 * « this draft is not a version yet », in exactly the spelling the server uses.
 *
 * What the schema refuses here is only ever a SHAPE. The four refusals of §9.1
 * - a protocol without a persona, a variable that does not resolve, a file that
 * is gone, two variables of one name - are legal shapes and refused REASONS:
 * they are named by `POST /api/experiences/check`, which the save box reads,
 * and folding them in here would grey the button without saying why.
 *
 * ## A new experience is born from the template of §5.4
 *
 * `newExperienceContent()` clones `DEFAULT_EXPERIENCE_PAGES`, so the empty
 * editor opens on `[Consentement et identité] -> [Protocole] -> [Retour
 * d'expérience]` (the third since issue #176) rather than on nothing. That makes the empty draft a FUNCTION and not a constant, like the
 * protocol's: the template is one object in one module, and an editor that
 * sorted its blocks in place would rewrite it for every experience created
 * after it in that process.
 */

export interface ExperienceDraft {
  readonly name: string;
  /** The short description of the cards and of a link's choice page (§13, §15). */
  readonly description: string;
  /** What the experience overrides of the PMS default (§9.4). Partial by design. */
  readonly theme: ExperienceTheme;
  /** The persona of §10.2: a reference plus the overlay, or nothing chosen yet. */
  readonly persona: ExperiencePersona | null;
  /** The Contexte of the protocol page (§9.5): served to the interviewer after the overlay. */
  readonly context: string;
  /**
   * The reference to the texts of the noyaux (noyau.md §3.5), or null. CARRIED
   * through the draft before any screen edits it (#147): a draft that dropped it
   * would save a version whose tag fell back to `production` in silence - the
   * forced `development` of the content it was opened from, lost on a keystroke.
   * Posed with the first Noyau block and removed with the last (N13,
   * `withNoyauTextsFollowingBlocks`), and its tag set from the inspector of any
   * of them (`withNoyauTextsPin`, #151).
   */
  readonly noyauTexts: NoyauTextsRef | null;
  /** The pages IN ORDER - the timeline draws this list, and the order is the meaning. */
  readonly pages: readonly ExperiencePageDraft[];
}

/**
 * A page nobody has typed yet: named by its rank, one empty temps, no blocks.
 *
 * `index` names it - « Page 3 » - rather than leaving it blank, because the
 * timeline labels a row with its name and a row labelled with nothing reads as
 * broken. It is a real value in the draft and not a placeholder: an operator who
 * keeps it gets a page called « Page 3 ».
 *
 * ONE temps, and it carries `FIRST_TEMPS_START`: `experienceContentSchema`
 * refuses a first temps that does not start with the page, so a page born with
 * an empty `temps: []` would be legal and a page born with a delayed first one
 * would not. Born with its temps, the page editor of #58 has somewhere to drop
 * a block on its first gesture.
 *
 * The two ids are struck by `crypto.randomUUID`, which is what the protocol's
 * editor already mints its step ids with: an id has to be unique inside ONE
 * content and nothing more (§8.6), because a variable token addresses a page or
 * a block within the experience that holds it.
 */
export function newContentPage(index: number): ExperiencePageDraft {
  return {
    id: crypto.randomUUID(),
    name: `Page ${index + 1}`,
    kind: 'content',
    autoDurationSeconds: null,
    transition: DEFAULT_PAGE_TRANSITION,
    visual: {},
    protocol: null,
    conversation: null,
    temps: [{ id: crypto.randomUUID(), speed: 'normal', start: FIRST_TEMPS_START, blocks: [] }],
  };
}

/** What the empty editor opens on: the template of §5.4, cloned (see the head). */
export function emptyExperienceDraft(): ExperienceDraft {
  const content = newExperienceContent('');
  return {
    name: '',
    description: '',
    theme: content.theme,
    persona: null,
    context: '',
    noyauTexts: null,
    pages: content.pages,
  };
}

/**
 * A draft from a stored content - a version's, or the server draft's.
 *
 * The parameter is `ExperienceDraftContent` - the shared name for a partial
 * content - and not a locally derived `Partial<ExperienceContent>`, because the
 * two are NOT the same type under `exactOptionalPropertyTypes`: the local one
 * promises that a present key holds a value, while a draft off the wire carries
 * `... | undefined`.
 *
 * A draft with NO page gets the template's, for the reason the head gives: a
 * timeline with no row has nothing to press, and §9.1 makes the protocol page
 * mandatory anyway.
 */
export function draftFromContent(content: ExperienceDraftContent): ExperienceDraft {
  const pages = content.pages ?? [];
  return {
    name: content.name ?? '',
    description: content.description ?? '',
    theme: content.theme ?? {},
    persona: content.persona ?? null,
    context: content.context ?? '',
    noyauTexts: content.noyauTexts ?? null,
    pages: pages.length === 0 ? newExperienceContent('').pages : [...pages],
  };
}

/**
 * What the editor opens on: the server draft when there is one, the
 * `développement` version otherwise, and the template for an experience that
 * does not exist yet (§4, §9.1).
 *
 * The order is the pattern's: a draft « protège d'un rechargement », so it wins
 * over the version it was started from.
 */
export function openingDraft(detail: ExperienceDetail | null): ExperienceDraft {
  if (detail === null) return emptyExperienceDraft();
  return draftFromContent(detail.draft?.content ?? detail.developmentContent);
}

/**
 * The draft as the PURE rules of `shared` read it - `namedVariablesOf`,
 * `variableReach`, `variableScopeBreach` all walk a content (§8.6).
 *
 * `ExperiencePageDraft` and `ExperiencePage` infer to the SAME TypeScript shape
 * - the draft schema loosens `min(1)` on the labels, which does not reach the
 * type - so the only gap is the `readonly` on the page list, and a spread
 * closes it. A spread and never a cast: the day a content gains a field the
 * compiler names this line rather than a rule somewhere that started reading
 * `undefined`. Nothing is trimmed or judged here - that is
 * `normalizedContent`'s - because the rules it feeds are about STRUCTURE
 * (which page declares what) and a blank name is a structure too.
 */
export function draftAsContent(draft: ExperienceDraft): ExperienceContent {
  return {
    name: draft.name,
    description: draft.description,
    theme: draft.theme,
    persona: draft.persona,
    context: draft.context,
    noyauTexts: draft.noyauTexts,
    pages: [...draft.pages],
  };
}

/** A page with its name trimmed - the one blank a draft holds and a version does not. */
function trimmedPage(page: ExperiencePageDraft): ExperiencePageDraft {
  return { ...page, name: page.name.trim() };
}

/**
 * The content a draft would become, or `null` when it could not become one.
 *
 * Trimmed first, then judged - the order `experienceContentSchema` states: a
 * name holding three spaces is a name an operator believes they wrote, and it
 * would reach the base as a blank. What answers is the SCHEMA itself; see the
 * head of this file for why it is asked rather than re-decided.
 */
export function normalizedContent(draft: ExperienceDraft): ExperienceContent | null {
  const candidate = {
    name: draft.name.trim(),
    description: draft.description.trim(),
    theme: draft.theme,
    persona: draft.persona,
    context: draft.context.trim(),
    noyauTexts: draft.noyauTexts,
    pages: draft.pages.map(trimmedPage),
  };
  const parsed = experienceContentSchema.safeParse(candidate);
  return parsed.success ? parsed.data : null;
}

/**
 * What an autosave SENDS - and the one thing about it that is not obvious.
 *
 * `experienceDraftContentSchema` makes each field optional WITHOUT loosening
 * it: `name` is still `z.string().trim().min(1)` when it is there. So an empty
 * name has to be OMITTED and cannot be sent as `''` - a draft of an experience
 * nobody has named yet would otherwise be answered with a 400, which is the one
 * refusal an autosave must never produce: it fires while somebody types, and a
 * red box on the third keystroke of an empty field teaches an operator that the
 * editor is broken.
 *
 * The DESCRIPTION goes the other way and is always sent, empty included: its
 * bound is `.max()` with a default of `''`, so a cleared description is a
 * legal value and omitting it would make « j'ai effacé la description »
 * unsaveable. Same for the pages, the theme and the persona: clearing the
 * persona is exactly the gesture §10.2 draws, and an omitted key would read as
 * « je n'y ai pas touché ». The Contexte of §9.5 follows the description: a
 * cleared Contexte is a legal, saveable value - and so does the reference to the
 * texts of the noyaux, whose null is « aucun bloc Noyau » (noyau.md §3.5).
 */
export function draftPayload(draft: ExperienceDraft): ExperienceDraftContent {
  const payload: ExperienceDraftContent = {
    description: draft.description.trim(),
    theme: draft.theme,
    persona: draft.persona,
    context: draft.context.trim(),
    noyauTexts: draft.noyauTexts,
    pages: draft.pages.map(trimmedPage),
  };
  const name = draft.name.trim();
  if (name !== '') payload.name = name;
  return payload;
}

/** Everything the versioned editor needs to run an experience's draft (§4). */
export const EXPERIENCE_DRAFT_KIT: VersionedDraftKit<
  ExperienceContent,
  ExperienceDraftContent,
  ExperienceDraft
> = {
  opening: openingDraft,
  fromContent: draftFromContent,
  normalized: normalizedContent,
  payload: draftPayload,
};

/* ---------------------------------------------------------------- the pages */

/**
 * « Ajouter une page » - one content page appended at the END of the line
 * (§9.1).
 *
 * At the end, and the « + » of an edge is the same splice aimed at a gap -
 * `insertPageAt` below. The ceiling is the schema's (`EXPERIENCE_PAGES_MAX`),
 * and it is enforced HERE rather than left to the save: a thirty-first page
 * that vanished on save would lose whatever was typed into it.
 */
export function addPage(draft: ExperienceDraft): ExperienceDraft {
  return insertPageAt(draft, draft.pages.length);
}

/**
 * Le « + » d'une arête : one content page INSERTED at `index` (§9.1, #57).
 *
 * `index` is a GAP and not a rank - 0 puts the page before the first, and
 * `pages.length` appends - which is why `addPage` is this function called at
 * the end rather than a second implementation of one splice.
 *
 * **Never AFTER the feedback page** (retour-experience.md §2, §7.1, issue
 * #176): it is always the last of the line, so a gap past it is clamped to the
 * gap JUST BEFORE it - « Ajouter une page » and the « + » of the end keep
 * working on an experience that has one, and they put the page before the
 * retour. The same clamp as the ceiling below: a gesture that cannot aim at a
 * gap lands on the nearest one rather than being dropped.
 *
 * The new page is NAMED BY THE LINE'S NEW LENGTH and not by its position, and
 * that is the whole reason this is not `newContentPage(at)`: a page inserted at
 * rank 2 of a line of four would otherwise be born « Page 3 » beside the page
 * already called « Page 3 », and the rail would show one name twice with
 * nothing to tell them apart. Named by the length, every insertion mints a name
 * the line does not already hold; the operator renames it in its own editor.
 *
 * An out-of-range index is CLAMPED and not refused. There is no gesture that
 * can aim at a gap the line has not got - the « + » sits on an edge that exists
 * - so a clamp only ever catches an arithmetic mistake, and catching it by
 * dropping the page silently would be the worse answer.
 */
export function insertPageAt(draft: ExperienceDraft, index: number): ExperienceDraft {
  if (draft.pages.length >= EXPERIENCE_PAGES_MAX) return draft;
  const feedbackAt = draft.pages.findIndex((page) => page.kind === 'feedback');
  const ceiling = feedbackAt === -1 ? draft.pages.length : feedbackAt;
  const at = Math.min(Math.max(Math.trunc(index), 0), ceiling);
  const pages = [...draft.pages];
  pages.splice(at, 0, newContentPage(draft.pages.length));
  return { ...draft, pages };
}

/**
 * Where `insertPageAt(draft, index)` actually puts its page - the gap AFTER the
 * clamps of that function (the line's two ends, and the feedback page's).
 *
 * The callers that select the page they have just created read it back from
 * the new draft at this rank, and `index` alone is no longer that rank: the
 * « + » after the last page of an experience that ends on its feedback page
 * asks for `pages.length`, which lands one place before.
 */
export function insertedPageIndex(draft: ExperienceDraft, index: number): number {
  const feedbackAt = draft.pages.findIndex((page) => page.kind === 'feedback');
  const ceiling = feedbackAt === -1 ? draft.pages.length : feedbackAt;
  return Math.min(Math.max(Math.trunc(index), 0), ceiling);
}

/**
 * Why the feedback page of the template cannot be added to this draft, in the
 * words of the tooltip, or null when it can (retour-experience.md §7.1).
 *
 * « Au plafond de 30 pages, "Page de retour" est désactivée avec la raison au
 * survol. » `shared` says WHICH obstacle (`feedbackPageObstacle`); the sentence
 * is the screen's. `present` is not reachable from the menu - the menu is only
 * offered while there is no feedback page - but the gesture below answers it
 * all the same, and a function that names one of two obstacles and silently
 * forgets the other is a trap for its next caller.
 */
export function feedbackPageRefusal(draft: ExperienceDraft): string | null {
  const obstacle = feedbackPageObstacle(draft.pages);
  if (obstacle === null) return null;
  return obstacle === 'present'
    ? 'Cette expérience a déjà sa page de retour : elle n’en porte qu’une.'
    : `L’expérience porte déjà ${EXPERIENCE_PAGES_MAX} pages, le maximum : supprime-en une pour ajouter la page de retour.`;
}

/**
 * « Page de retour » du menu du « + » de fin : the feedback page of the
 * template, added LAST (retour-experience.md §7.1).
 *
 * `withFeedbackPage` of `shared` does the whole of it - the template cloned
 * with fresh ids, its variable names suffixed against the timeline - and is the
 * very function the migration of the drafts runs, so the two cannot disagree on
 * what « la page de retour du gabarit » is. A refused gesture (one already
 * there, or the ceiling) answers the draft UNCHANGED, like `addPage`.
 */
export function addFeedbackPage(draft: ExperienceDraft): ExperienceDraft {
  return withFeedbackPage(draft) ?? draft;
}

/** The phrase of thanks of a feedback page, replaced whole (retour-experience.md §2, §7.2). */
export function withPageThanks(
  draft: ExperienceDraft,
  pageId: string,
  thanks: string,
): ExperienceDraft {
  return {
    ...draft,
    pages: draft.pages.map((page) =>
      page.id === pageId && page.kind === 'feedback' ? { ...page, thanks } : page,
    ),
  };
}

/**
 * Whether the corbeille of the scene may remove this page (§9.1).
 *
 * Two refusals, both sentences of §9.1 rather than defensiveness. The PROTOCOL
 * page « est obligatoire, unique, et ne se supprime pas ». And an experience
 * keeps at least one page of CONTENT: a line made of the protocol page alone is
 * a content the schema would still accept, but it is an experience with nothing
 * before the conversation, and the click that produced it would have no way
 * back - « Ajouter une page » puts the new page at the end, behind the
 * protocol.
 *
 * A BOOLEAN rather than « remove and compare », because the scene has to draw
 * the corbeille absent - « aucune corbeille » on the protocol page - before
 * anybody presses anything.
 *
 * The FEEDBACK page is always removable (retour-experience.md §2, §7.1: « la
 * croix la supprime »), and it has to be said BEFORE the count of content pages
 * below: that count asks `kind === 'content'` of the page it keeps, so on
 * `[content, protocol, feedback]` the feedback page was refused for want of a
 * second CONTENT page - a rule about the last page of content, which the
 * feedback page is not. It leaves nothing behind that « Ajouter une page »
 * cannot restore, since the « + » of the end offers it back.
 */
export function canRemovePage(draft: ExperienceDraft, pageId: string): boolean {
  const page = draft.pages.find((one) => one.id === pageId);
  if (page === undefined || page.kind === 'protocol') return false;
  if (page.kind === 'feedback') return true;
  return draft.pages.filter((one) => one.kind === 'content').length > 1;
}

/**
 * The corbeille itself. A no-op on a page `canRemovePage` refuses - see it. A
 * page that took the last Noyau block with it takes the reference to the texts
 * too (N13).
 */
export function removePage(draft: ExperienceDraft, pageId: string): ExperienceDraft {
  if (!canRemovePage(draft, pageId)) return draft;
  return withNoyauTextsFollowingBlocks({
    ...draft,
    pages: draft.pages.filter((one) => one.id !== pageId),
  });
}

/* ------------------------------------------------ the texts of the noyaux */

/**
 * The reference to the texts of the noyaux FOLLOWING the blocks (noyau.md
 * §3.5, N13): posed at `latest` - the default of every reference (vitrine.md
 * V16) - as soon as a Noyau block exists, removed with the last one, and left as
 * it is otherwise, so a pinned version survives every gesture that keeps a
 * block.
 *
 * Run by every gesture that can add or remove a block - `withPage` for the
 * editor of a page, `removePage` for the Timeline - rather than at the save:
 * the inspector of a block reads the reference as the draft holds it, and a
 * reference that only appeared on the wire would be a pin the screen could not
 * show.
 */
export function withNoyauTextsFollowingBlocks(draft: ExperienceDraft): ExperienceDraft {
  const carries = noyauBlocksOf(draftAsContent(draft)).length > 0;
  if (carries && draft.noyauTexts === null) return { ...draft, noyauTexts: { pin: 'latest' } };
  if (!carries && draft.noyauTexts !== null) return { ...draft, noyauTexts: null };
  return draft;
}

/**
 * The pin the texts of the noyaux are read at (§3.5, vitrine.md §3.1), set from
 * the inspector of ANY Noyau block: the reference is the experience's, one for
 * all of them.
 */
export function withNoyauTextsPin(draft: ExperienceDraft, pin: VersionPin): ExperienceDraft {
  return { ...draft, noyauTexts: { pin } };
}

/**
 * « Remplacer » on the protocol page: the reference it plays, or null for
 * « sans protocole » (§9.5).
 *
 * Addressed by PAGE ID and not by « the protocol page », though §9.1 makes that
 * page unique: the page editor of #58 writes this from a screen that holds one
 * page and knows its id, and a function that hunted for the protocol page would
 * be a second place deciding which page that is.
 *
 * A no-op on a content page. `protocol` is meaningful on the protocol page
 * alone (`experiencePageSchema`), so writing it elsewhere would store a
 * reference the extractor of §4 then has to ignore.
 */
export function withPageProtocol(
  draft: ExperienceDraft,
  pageId: string,
  protocol: VersionedRef | null,
): ExperienceDraft {
  return {
    ...draft,
    pages: draft.pages.map((page) =>
      page.id === pageId && page.kind === 'protocol' ? { ...page, protocol } : page,
    ),
  };
}

/** The experience's own tokens, replaced whole - what the « Visuel global » bar writes (§9.4). */
export function withTheme(draft: ExperienceDraft, theme: ExperienceTheme): ExperienceDraft {
  return { ...draft, theme };
}

/**
 * What ONE page overrides of that theme, replaced whole (§9.4).
 *
 * It lives here and not in the page editor because the bar of §9.4 is ONE
 * component at both levels: the Timeline mounts it on `draft.theme`, the page
 * editor of #58 on `page.visual`, and both hand back a whole `ExperienceTheme`.
 * Two writers would be two answers to « qu'est-ce que cette page surcharge ».
 */
export function withPageVisual(
  draft: ExperienceDraft,
  pageId: string,
  visual: ExperienceTheme,
): ExperienceDraft {
  return {
    ...draft,
    pages: draft.pages.map((page) => (page.id === pageId ? { ...page, visual } : page)),
  };
}

/**
 * WHICH page the scene shows: the one `?page=` names, or the first (§9.1).
 *
 * The URL is the source - « un rechargement, un retour arrière du navigateur ou
 * un lien collé dans un fil d'équipe retombent sur la même page » - and an id
 * naming no page degrades to the FIRST rather than to an empty stage: a link
 * pasted after the page it named was deleted is exactly that case, and it is a
 * stale link, not an error state.
 *
 * Null only when the draft holds no page at all, which `draftFromContent`
 * already makes unreachable. It is what keeps the scene from indexing into
 * nothing, not a case somebody can produce.
 */
export function selectedPage(
  draft: ExperienceDraft,
  requested: string | null,
): ExperiencePageDraft | null {
  const named = requested === null ? undefined : draft.pages.find((one) => one.id === requested);
  return named ?? draft.pages[0] ?? null;
}

/**
 * How many blocks a page holds, across every temps (§9.3).
 *
 * The timeline shows it beside the page's name, and it is a COUNT and not a
 * list: a page is read in its own editor, and the row is there to say « this
 * one is empty » at a glance.
 */
export function blockCount(page: ExperiencePageDraft): number {
  return page.temps.reduce((total, temps) => total + temps.blocks.length, 0);
}

/**
 * The persona of the draft with one field replaced, or a fresh one.
 *
 * Two gestures write it - « Remplacer le persona » and the tag toggle - and
 * both have to keep the overlay: §10.2 makes the overlay a property of the
 * EXPERIENCE and not of the persona it prolongs, so changing who is prolonged
 * must not silently drop what was written about them.
 */
export function withPersonaRef(draft: ExperienceDraft, ref: VersionedRef): ExperienceDraft {
  return { ...draft, persona: { ref, overlay: draft.persona?.overlay ?? '' } };
}

/**
 * The overlay, written onto the persona the draft already carries.
 *
 * A no-op when there is none, and that is §10.2 rather than defensiveness: « la
 * surcouche est inactive » in the empty state, because a text prolonging
 * nobody has no place to be stored - the persona field is `{ ref, overlay }`
 * and there is no ref.
 */
export function withPersonaOverlay(draft: ExperienceDraft, overlay: string): ExperienceDraft {
  if (draft.persona === null) return draft;
  return { ...draft, persona: { ...draft.persona, overlay } };
}

/**
 * Whether the edge that LEADS to `pages[index]` stays in the thread
 * (prolongation.md §6, issue #186): the page at `index` is one of the pages that
 * follow the protocol page directly, so the page before it is the protocol page or
 * another page of the thread, and no transition is played between the two.
 *
 * The edge is « dans le fil » and the rail's dash turns navy on this answer. A
 * ticked page OUTSIDE the run is not in the thread yet - the save refuses it - so
 * its edge keeps the transition it carries.
 */
export function edgeInThread(pages: readonly ExperiencePageDraft[], index: number): boolean {
  const page = pages[index];
  if (index <= 0 || page === undefined) return false;
  return conversationRunOf(pages).includes(page.id);
}
